import { useRef } from "react";
import { useDrop } from "react-dnd/dist/hooks";
import {
    Checkbox,
    FormControlLabel,
    IconButton,
    Radio,
    TextField
} from "@mui/material";
import SettingsIcon from "@mui/icons-material/Settings";
import CloseIcon from "@mui/icons-material/Close";
import {
    Control,
    ItemTypes,
    PageField,
    Panel,
    isField,
    isControl,
    RelatedTableControl
} from "../../types/interfaces";

interface PanelColumnProps {
    panel: Panel;
    colIndex: number;
    panelPath: number[];
    content: Array<{ id: string; field?: PageField | Control | Panel; column: number; row?: number }>;
    onDrop: (item: PageField | Control | Panel, panelPath: number[], colIndex: number) => void;
    onOpenSettings: (item: PageField | Control | Panel) => void;
    onDelete: (panel: Panel, controlId: string) => void;
    selectedControl?: PageField | Control | Panel | RelatedTableControl | null;
}

const PanelColumn: React.FC<PanelColumnProps> = ({
    onDrop,
    content,
    panel,
    colIndex,
    onOpenSettings,
    onDelete,
    panelPath,
    selectedControl
}) => {
    const ref = useRef<HTMLDivElement>(null);

    const [{ isOver }, drop] = useDrop({
        accept: [ItemTypes.FIELD, ItemTypes.CONTROL],
        drop: (item, monitor) => {
            // Only handle drop in the current column if it's the *shallowest* hovered
            if (monitor.isOver({ shallow: true })) {
                onDrop(item as PageField | Control | Panel, panelPath, colIndex);
            }
        },
        collect: (monitor) => ({
            isOver: monitor.isOver({ shallow: true }),
        }),
    });

    drop(ref);

    return (
        <div ref={ref} className={`panel-column ${isOver ? "hovered" : ""}`}>
            {content.length === 0 ? (
                <div className="placeholder">Drop here</div>
            ) : (
                content.map((c) => {
                    const field = c.field;
                    if (!field) return null;

                    const isSelected = selectedControl?.fieldID === field.fieldID;

                    // === Nested Panel Rendering ===
                    if (field.type === "PANEL" && "controls" in field) {
                        const nestedPanel = field as Panel;
                        return (
                            <div
                                key={c.id}
                                className={`panel-component nested-panel ${isSelected ? "selected" : ""}`}
                            >
                                <div className="panel-header-toolbar">
                                    <div>{nestedPanel.name || nestedPanel.id} ({nestedPanel.columns} Cols)</div>
                                    <div>
                                        <IconButton size="small" onClick={() => onOpenSettings(nestedPanel)}>
                                            <SettingsIcon fontSize="small" />
                                        </IconButton>
                                        <IconButton size="small" onClick={() => onDelete(panel, c.id)}>
                                            <CloseIcon fontSize="small" />
                                        </IconButton>
                                    </div>
                                </div>

                                {Array.from({ length: nestedPanel.rows || 1 }).map((_, rowIndex) => (
                                    <div key={rowIndex} className="panel-columns">
                                        {Array.from({ length: nestedPanel.columns || 1 }).map((_, subCol) => {
                                            const nestedContent = nestedPanel.controls.filter(
                                                sub => sub.column === subCol && sub.row === rowIndex
                                            );

                                            return (
                                                <PanelColumn
                                                    key={`${rowIndex}-${subCol}`}
                                                    panel={nestedPanel}
                                                    colIndex={subCol}
                                                    // 👇 Ensure proper nested path here
                                                    panelPath={[...panelPath, rowIndex, subCol]}
                                                    content={nestedContent}
                                                    onDrop={onDrop}
                                                    onOpenSettings={onOpenSettings}
                                                    onDelete={onDelete}
                                                    selectedControl={selectedControl}
                                                />
                                            );
                                        })}
                                    </div>
                                ))}
                            </div>
                        );
                    }

                    // === Regular Controls / Fields / Related Table ===
                    return (
                        <div
                            key={c.id}
                            className={`control-box ${isSelected ? "selected" : ""}`}
                        >
                            <div className="control-toolbar">
                                <IconButton size="small" onClick={() => onOpenSettings(field)}>
                                    <SettingsIcon fontSize="small" />
                                </IconButton>
                                <IconButton size="small" onClick={() => onDelete(panel, c.id)}>
                                    <CloseIcon fontSize="small" />
                                </IconButton>
                                <span className="drag-handle">⬍</span>
                            </div>

                            <div className="control-preview">
                                {field.fieldName && <div className="control-header">{field.fieldName}</div>}

                                {isField(field) ? (
                                    <TextField
                                        label={`{Field:${field.fieldName}}`}
                                        fullWidth
                                        size="small"
                                        disabled
                                    />
                                ) : field.type === "TABLE" ? (
                                    <div className="related-table-placeholder">
                                        <div className="control-header">
                                            Related Table: {field.name || "Unnamed Related Table"}
                                        </div>
                                        <div className="fake-table-grid">
                                            {[...Array(4)].map((_, r) => (
                                                <div className="fake-table-row" key={r}>
                                                    {[...Array(3)].map((_, c) => (
                                                        <div className="fake-table-cell" key={c}></div>
                                                    ))}
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                ) : isControl(field) ? (() => {
                                    const control = field as Control;
                                    switch (control.type) {
                                        case "TEXTBOX":
                                            return <TextField label={control.label || "Textbox"} fullWidth size="small" disabled />;
                                        case "CHECKBOX":
                                            return <FormControlLabel control={<Checkbox disabled size="small" />} label={control.label || "Checkbox"} />;
                                        case "RADIO":
                                            return <FormControlLabel control={<Radio disabled size="small" />} label={control.label || "Radio"} />;
                                        case "LISTBOX":
                                        case "COMBOBOX":
                                            return (
                                                <TextField
                                                    select
                                                    label={control.label || "Select"}
                                                    fullWidth
                                                    size="small"
                                                    disabled
                                                    SelectProps={{ native: true }}
                                                >
                                                    <option>Option 1</option>
                                                    <option>Option 2</option>
                                                    <option>Option 3</option>
                                                </TextField>
                                            );
                                        case "LINE":
                                            return <hr style={{ borderTop: "1px solid #999", margin: "8px 0" }} />;
                                        case "LABEL":
                                            return <div style={{ fontWeight: "bold", fontSize: "0.9rem" }}>{control.label || "Label Text"}</div>;
                                        case "CUSTOM_HTML":
                                            return (
                                                <div style={{ fontStyle: "italic", padding: "6px", border: "1px dashed #ccc" }}>
                                                    {control.html || "<custom html>"}
                                                </div>
                                            );
                                        default:
                                            return (
                                                <TextField
                                                    label={control.label || "Control"}
                                                    fullWidth
                                                    size="small"
                                                    disabled
                                                />
                                            );
                                    }
                                })() : null}
                            </div>
                        </div>
                    );
                })
            )}
        </div>
    );
};

export default PanelColumn;
